<template>
  <div>
    <div
        class="mt-input"
        :class="{ disabled, active, filled, invalid }"
    >
      <div
          v-if="coins"
          class="coins"
      >{{ coins }}</div>

      <div
          v-if="label"
          class="label"
          :class="{ 'hold-place': !filled, invalid }"
      >{{ label }}<span class="required" v-if="required">&nbsp;&nbsp;*</span></div>

        <input
            v-if="isMobile"
            class="mt-input__value"
            type="time"
            :class="{ disabled, invalid }"
            :value="innerValue"
            @change="setMobileValue"
            ref="field"
            :disabled="disabled"
            @blur="active = false"
        />
        <input
            v-else
            class="mt-input__value"
            type="text"
            :class="{ disabled, invalid }"
            :value="innerValue"
            ref="field"
            :disabled="disabled"
            @blur="active = false"
            @focus="onFocus"
            readonly
            @keydown.stop="onKeyDown"
            @click="onClick"
        />
    </div>
    <div
        v-if="errorMessage"
        class="mt-invalid"
    >
        <div class="mt-invalid-text">
            {{ errorMessage }}
        </div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';

const getters = [
    'isMobile',
];

export default {
    name: 'ModelString',
    props: {
        coins: Number,
        label: String,
        disabled: {
            type: Boolean,
            default: false,
        },
        valid: {
            type: Boolean,
            default: true,
        },
        invalidateFunction: {
            type: Function,
            default: null,
        },
        required: {
            type: Boolean,
            default: false,
        },
        value: {
            type: Array,
            required: true,
        },
    },
    model: {
        prop: 'value',
        event: 'change',
    },
    data() {
        return {
            mobileValue: null,
            active: false,
            // block:
            fresh: true,
            block: 0,
            blocks: [
                { leadingZero: false, sync: 'hours', size: 2, max: 23, value: null, holder: '-', append: ':' },
                { leadingZero: false, sync: 'minutes', size: 2, max: 59, value: null, holder: '-' },
            ],
        };
    },
    watch: {
        block(_, before) {
            this.blocks[before].leadingZero = false;
            this.fresh = true;
        },
        value(time) {
            this.syncBlockValue('hours', time[0]);
            this.syncBlockValue('minutes', time[1]);
        },
    },
    mounted() {
        this.syncBlockValue('hours', this.value[0]);
        this.syncBlockValue('minutes', this.value[1]);
    },
    computed: {
        ...mapGetters(getters),

        innerValue() {
            return this.blocks.map(block => {
                let value = (block.value !== null)
                    ? block.value.toString()
                        .padStart(block.size, '0')
                    : ''.padStart(block.size, block.holder);
                return value + (block.append || '');
            }).join('');
        },

        filled() {
            return this.active || this.blocks.some(v => v.value !== null) || this.isMobile;
        },

        invalid() {
            return !this.valid || (this.invalidateFunction && this.invalidateFunction(this.value));
        },

        errorMessage() {
            return this.invalidateFunction ? this.invalidateFunction(this.value) : '';
        },

        selectionStart() {
            let offset = 0;
            for (let blockIndex = 0; blockIndex < this.block; blockIndex++) {
                let { size } = this.blocks[blockIndex];
                offset += size + 1;
            }
            return offset;
        },

        selectionEnd() {
            let offset = 0;
            for (let blockIndex = 0; blockIndex <= this.block; blockIndex++) {
                let { size, append } = this.blocks[blockIndex];
                offset += size + (append || '').length;
            }
            offset -= (this.blocks[this.block].append || '').length;
            return offset;
        },
    },
    methods: {
        emitModel() {
            this.$emit('change', this.blocks.map(v => v.value));
        },
        setMobileValue(e) {
            let v = (e.target.value || ':')
                .split(':')
                .map(v => {
                    v = parseInt(v);
                    return isNaN(v) ? null : v;
                });
            this.blocks.forEach((_, index) => {
                this.blocks[index].value = v[index];
                this.emitModel();
            });
        },
        setBlockValue(value) {
            if (this.blocks[this.block].value === value) {
                return;
            }
            this.blocks[this.block].value = value;
            this.emitModel();
        },
        syncBlockValue(key, value) {
            let i = this.blocks.findIndex(v => v.sync === key);
            if (i === -1) {
                return;
            }
            if (value === null) {
                this.blocks[i].value = value;
            } else {
                this.blocks[i].value = Math.min(value, this.blocks[i].max);
            }
        },
        onFocus() {
            this.active = true;
            this.block = 0;
            this.drawSelection();
        },
        onClick() {
            let selectionStart = this.$refs.field.selectionStart;
            let offset = 0;
            for (let blockIndex = 0; blockIndex < this.blocks.length; blockIndex++) {
                let { size } = this.blocks[blockIndex];
                if (offset + size >= selectionStart) {
                    this.block = blockIndex;
                    break;
                }
                offset += size + 1;
            }
            this.drawSelection();
        },
        onKeyDown(e) {
            //Digits
            if (e.key >= '0' && e.key <= '9') {
                let blockConfig = this.blocks[this.block];
                let value;
                let parts = this.innerValue.split(':');

                if (this.fresh) {
                    value = parseInt(e.key);
                    this.fresh = false;
                } else {
                    value = parseInt((parts[this.block] + e.key)
                        .slice(1));
                }

                value = Math.min(value, blockConfig.max);

                this.setBlockValue(value);

                if (parseInt(`${value}0`) > blockConfig.max || blockConfig.leadingZero) {
                    this.moveBlock(1);
                }

                if (e.key === '0') {
                    this.blocks[this.block].leadingZero = true;
                }

                this.drawSelection();
            }

            // Arrows
            if (e.keyCode === 37 || e.keyCode === 39) {
                this.moveBlock(e.keyCode - 38);
                this.drawSelection();
            }
            // Tab
            if (e.keyCode === 9) {
                if (this.block + 1 < this.blocks.length) {
                    e.preventDefault();
                }
                this.moveBlock(1);
                this.drawSelection();
            }

            // Backspace
            if (e.keyCode === 8) {
                this.clear();
            }
        },
        clear() {
            this.fresh = true;
            let parts = this.innerValue.split(':');
            if (isNaN(parseInt(parts[this.block]))) {
                this.moveBlock(-1);
            } else {
                this.setBlockValue(null);
            }
            this.drawSelection();
        },
        moveBlock(direction) {
            if (direction < 0) {
                this.block = Math.max(this.block - 1, 0);
            }
            if (direction > 0) {
                this.block = Math.min(this.block + 1, this.blocks.length - 1);
            }
        },
        drawSelection() {
            this.$nextTick(() => {
                this.$refs.field.setSelectionRange(this.selectionStart, this.selectionEnd);
            });
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../../../styles/mt';
</style>
